import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useStable } from "../context/StableContext";
import api, { errText } from "../lib/api";
import { APP_VERSION } from "../constants/appVersion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Bug, Lightbulb, Loader2, Rocket, X } from "lucide-react";
import { toast } from "sonner";

function gatherContext(pathname) {
  const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
  return {
    route: pathname,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    user_agent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    standalone: !!standalone,
    app_version: APP_VERSION,
  };
}

const fold = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export default function FeedbackDialog({ open, onOpenChange, roadmapItem = null, defaultType = "bug" }) {
  const { current } = useStable();
  const location = useLocation();
  const [type, setType] = useState(defaultType);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [attempted, setAttempted] = useState("");
  const [busy, setBusy] = useState(false);
  // roadmap linking + keyword suggestion
  const [linked, setLinked] = useState(roadmapItem);
  const [roadmap, setRoadmap] = useState([]);
  const [suggestion, setSuggestion] = useState(null);
  const dismissed = useRef(new Set());

  useEffect(() => { if (open) { setLinked(roadmapItem); setType(defaultType); } }, [open, roadmapItem, defaultType]);

  useEffect(() => {
    if (!open || roadmapItem) return; // when opened from a roadmap card we already have context
    api.get("/roadmap").then((r) => setRoadmap(r.data || [])).catch(() => {});
  }, [open, roadmapItem]);

  // Non-blocking, debounced keyword suggestion (client-side, accent-tolerant, case-insensitive).
  useEffect(() => {
    if (!open || linked || roadmapItem) { setSuggestion(null); return; }
    const t = setTimeout(() => {
      const text = fold(`${title} ${description}`);
      if (text.trim().length < 4) { setSuggestion(null); return; }
      const hit = roadmap.find((it) => !dismissed.current.has(it.id)
        && (it.keywords || []).some((k) => k && k.length >= 3 && text.includes(fold(k))));
      setSuggestion(hit || null);
    }, 500);
    return () => clearTimeout(t);
  }, [title, description, roadmap, open, linked, roadmapItem]);

  const reset = () => { setType(defaultType); setTitle(""); setDescription(""); setAttempted(""); setLinked(roadmapItem); setSuggestion(null); dismissed.current = new Set(); };
  const close = (o) => { if (!o) { onOpenChange(false); reset(); } };

  const submit = async () => {
    if (!title.trim() || !description.trim()) { toast.error("Titolo e descrizione sono obbligatori."); return; }
    setBusy(true);
    try {
      await api.post("/feedback", {
        type, title: title.trim(), description: description.trim(),
        attempted_action: type === "bug" ? attempted.trim() : undefined,
        stable_id: current?.id || undefined,
        roadmap_item_id: linked?.id || undefined,
        technical_context: gatherContext(location.pathname),
      });
      toast.success("Grazie! La tua segnalazione è stata inviata.");
      onOpenChange(false); reset();
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const TypeBtn = ({ value, icon: Icon, label }) => (
    <button type="button" data-testid={`feedback-type-${value}`} onClick={() => setType(value)}
      className={`flex-1 flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-medium transition-colors ${type === value ? "border-[#1B3B2B] bg-[#1B3B2B] text-[#FAF8F5]" : "border-[#E5DEC9] bg-white text-[#1B3B2B] hover:border-[#8C532B]"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Invia un feedback</DialogTitle>
          <DialogDescription className="text-xs">
            Aiutaci a migliorare Equiro. Non raccogliamo screenshot né i contenuti della pagina, solo informazioni tecniche di base per riprodurre il problema.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {linked && (
            <div data-testid="feedback-roadmap-context" className="flex items-center gap-2 rounded-lg bg-[#F0EAE1] border border-[#E5DEC9] px-3 py-2 text-sm text-[#1B3B2B]">
              <Rocket className="h-4 w-4 text-[#8C532B] shrink-0" />
              <span className="min-w-0 flex-1">Feedback relativo a: <strong>{linked.title}</strong></span>
              {!roadmapItem && (
                <button type="button" data-testid="feedback-roadmap-unlink" onClick={() => setLinked(null)} className="text-[#8C6D4F] hover:text-[#5A3419] shrink-0"><X className="h-4 w-4" /></button>
              )}
            </div>
          )}
          <div className="flex gap-2" data-testid="feedback-type-group">
            <TypeBtn value="bug" icon={Bug} label="Segnala un problema" />
            <TypeBtn value="suggestion" icon={Lightbulb} label="Suggerimento" />
          </div>
          <div>
            <Label htmlFor="fb-title">Titolo</Label>
            <Input id="fb-title" data-testid="feedback-title" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder={type === "bug" ? "Es. Il calendario non salva la lezione" : "Es. Aggiungere un promemoria vaccini"} className="mt-1" />
          </div>
          <div>
            <Label htmlFor="fb-desc">Descrizione</Label>
            <Textarea id="fb-desc" data-testid="feedback-description" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrivi con parole tue cosa è successo o cosa vorresti." className="mt-1 min-h-[110px]" />
          </div>
          {suggestion && !linked && (
            <div data-testid="feedback-roadmap-suggestion" className="rounded-lg border border-[#E5DEC9] bg-[#FCFAF6] px-3 py-2.5">
              <div className="flex items-start gap-2">
                <Rocket className="h-4 w-4 text-[#8C532B] mt-0.5 shrink-0" />
                <div className="min-w-0 text-sm">
                  <p className="text-[#1B3B2B]">Stavi pensando a <strong>{suggestion.title}</strong>?</p>
                  <p className="text-xs text-[#5F6B64] mt-0.5">Questa funzione è già tra le aree previste per Equiro. Puoi comunque inviare il tuo feedback così com'è.</p>
                  <div className="flex flex-wrap gap-3 mt-1.5">
                    <a href="/in-arrivo" target="_blank" rel="noreferrer" data-testid="feedback-suggestion-more" className="text-xs font-semibold text-[#8C532B] hover:text-[#5A3419] underline underline-offset-2">Scopri di più</a>
                    <button type="button" data-testid="feedback-suggestion-link" onClick={() => { setLinked({ id: suggestion.id, title: suggestion.title }); setSuggestion(null); }} className="text-xs font-semibold text-[#1B3B2B] hover:text-[#8C532B]">Collega il feedback</button>
                    <button type="button" data-testid="feedback-suggestion-dismiss" onClick={() => { dismissed.current.add(suggestion.id); setSuggestion(null); }} className="text-xs text-[#9AA39C] hover:text-[#5F6B64]">Ignora</button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {type === "bug" && (
            <div>
              <Label htmlFor="fb-attempted">Cosa stavi cercando di fare? <span className="text-[#9AA39C] font-normal">(facoltativo)</span></Label>
              <Textarea id="fb-attempted" data-testid="feedback-attempted" value={attempted} onChange={(e) => setAttempted(e.target.value)}
                placeholder="Es. Stavo registrando una terapia dal dettaglio cavallo." className="mt-1 min-h-[70px]" />
            </div>
          )}
        </div>
        <p className="text-xs text-[#5F6B64] mt-1" data-testid="feedback-tech-note">
          Per aiutarci a diagnosticare il problema, Equiro allega automaticamente alcune informazioni tecniche sull'app e sul dispositivo (pagina, versione, browser, fuso orario).
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} data-testid="feedback-cancel">Annulla</Button>
          <Button onClick={submit} disabled={busy} data-testid="feedback-submit" className="bg-[#1B3B2B] hover:bg-[#142E1F]">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Invia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
